import React, {FC, useRef} from "react";
import Grid from "@mui/material/Grid";
import {CalendarMonth} from "mdi-material-ui";
import Avatar from "@mui/material/Avatar";
import {styled} from "@mui/material/styles";
import Tooltip, {TooltipProps, tooltipClasses} from "@mui/material/Tooltip";
import {useSelector} from "../../store";
import {max} from "lodash";
import {useTranslation} from "next-i18next";
import {Box} from "@mui/material";


const MONTHS_ABBR = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
];

const StyledMonthAvatar = styled(Avatar)(({theme}) => ({
    margin: "0 auto",
    color: "white",
    textAlign: "center",
    fontWeight: 500,
    padding: 2,
    // cursor: "pointer",
    width: "26px",
    height: "26px",
    fontSize: 12,
    [theme.breakpoints.down("sm")]: {
        width: "22px",
        height: "22px",
        fontSize: 11,
        lineHeight: 12,
    },

}))

const HtmlTooltip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}}/>
))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        fontFamily: "Neutra Text TF",
        fontWeight: 300,
        marginTop: 12, //sets distance of popper from the element
        marginBottom: 0, // prevents container boundary from pushing in too far
        lineHeight: "1.15rem",
        [theme.breakpoints.down("sm")]: {
            marginTop: 10,
            fontSize: "1.0rem",
            lineHeight: "1.0rem",
        },
        backgroundColor: "#f6f6f6",
        color: "rgba(0, 0, 0, 0.87)",
        maxWidth: "none",
        fontSize: "1.1rem",
        border: "1px solid #1e1e1e",
        borderRadius: 2,
    }
}));

interface MonthConditionsInterface {
    backgroundColor: string,
    borderColor: string,
    translationKey: "month-conditions.no-diving" | "month-conditions.shoulder" | "month-conditions.good" | "month-conditions.excellent"
}

const monthConditions: MonthConditionsInterface[] = [
    {
        backgroundColor: "#a26d6f",
        borderColor: "#a26d6f",
        translationKey: "month-conditions.no-diving",
    },
    {
        backgroundColor: "#0d8093",
        borderColor: "#c3bc21",
        translationKey: "month-conditions.shoulder",
    },
    {
        backgroundColor: "#0d8093",
        borderColor: "#0d8093",
        translationKey: "month-conditions.good",
    },
    {
        backgroundColor: "#0d8093",
        borderColor: "#4ac52c",
        translationKey: "month-conditions.excellent",
    },
];

const createMonthStyle = (val: any) => {
    const {backgroundColor, borderColor} = monthConditions[val || 0];

    const background = `radial-gradient(circle, ${backgroundColor} 0%, ${backgroundColor} 50%, ${borderColor} 58%, ${borderColor} 75%, #fff 100%)`;

    return {background};
};


interface BestMonthsProps {
    bestMonths: { [id: string]: number }
}


export const BestMonths: FC<BestMonthsProps> = ({bestMonths}) => {

    const {t, i18n} = useTranslation();

    const timer = useRef();

    const boundaryRef = useRef();

    const {criteria} = useSelector((state) => state.trips);

    const [selectedYear, selectedMonth] = criteria.selectedPeriod.split("-");

    const [openMonths, setOpenMonths] = React.useState(
        MONTHS_ABBR.map((d) => {
            return d === selectedMonth;
        })
    );

    const handleClose = (monthIdx: number) => {
        openMonths[monthIdx] = false;
        setOpenMonths([...openMonths]);

        if (timer.current) clearTimeout(timer.current);

        // @ts-ignore
        timer.current = setTimeout(() => {
            if (!max(openMonths) && !!selectedMonth) {
                openMonths[MONTHS_ABBR.indexOf(selectedMonth)] = true;
                setOpenMonths([...openMonths]);
            }
        }, 1500);
    };

    const handleOpen = (monthIdx: number) => {
        openMonths[monthIdx] = true;
        setOpenMonths(
            openMonths.map((el, idx) => {
                return monthIdx === idx;
            })
        );
    };

    return (<>
        <Box
            ref={boundaryRef}
            sx={{
                pt: {
                    xs: 1,
                    sm: 1
                },
                pb: {
                    xs: 4,
                    sm: 4
                }
            }}>
            <Grid container
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  wrap={"nowrap"}
                  sx={{
                      overflowX: "auto"
                  }}
            >

                <Grid
                    item
                    sx={{
                        display: {
                            xs: "none",
                            sm: "block"
                        },
                        pt: 1,

                        flex: 1,
                    }}
                >
                    <CalendarMonth htmlColor={"#666"}/>
                </Grid>


                {MONTHS_ABBR.map((month, idx) => {

                    return (
                        <Grid
                            item
                            key={idx}
                            // onClick={() => handleMonthClicked(idx)}
                            style={{flex: 1}}
                        >
                            <HtmlTooltip
                                disableFocusListener
                                disableTouchListener
                                open={openMonths[idx]}
                                onClose={() => handleClose(idx)}
                                onOpen={() => handleOpen(idx)}

                                PopperProps={{
                                    sx: {
                                        zIndex: 0
                                    },
                                    disablePortal: true,
                                    modifiers: [
                                        {
                                            name: "offset",
                                            enabled: true,
                                            options: {
                                                offset: [8, -5]
                                            }
                                        },
                                        // {
                                        //     name: "flip",
                                        //     enabled: true,
                                        //     options: {
                                        //         fallbackPlacements: ["bottom"],
                                        //         boundary: boundaryRef.current
                                        //     }
                                        // },
                                        {
                                            name: "preventOverflow",
                                            enabled: true,
                                            options: {
                                                altAxis: false,
                                                altBoundary: false,
                                                tether: true,
                                                rootBoundary: boundaryRef.current,
                                                padding: 4,
                                            },
                                        },
                                        // {
                                        //     name: "preventOverflow",
                                        //     options: {
                                        //         boundary: boundaryRef.current,
                                        //     }
                                        // }
                                    ]
                                }}
                                title={
                                    <Grid container alignItems={"center"}>
                                        <Grid item>
                                            <StyledMonthAvatar
                                                sx={{
                                                    ...(createMonthStyle(bestMonths[month])),
                                                    margin: "2px",
                                                    width: {
                                                        xs: "12px",
                                                        sm: "16px"
                                                    },
                                                    height: {
                                                        xs: "12px",
                                                        sm: "16px"
                                                    },
                                                }}
                                            >
                                                {" "}
                                            </StyledMonthAvatar>
                                        </Grid>
                                        <Grid item>
                                            &nbsp;
                                            {t(
                                                // @ts-ignore
                                                monthConditions[bestMonths[month] || 0]
                                                    .translationKey
                                            )}
                                        </Grid>
                                        <Grid item>
                                            {/*<AvailabilityIndicator*/}
                                            {/*  tripId={diveTrip.tripId}*/}
                                            {/*  onUpdate={handleAvailUpdate}*/}
                                            {/*  divePeriod={`${selectedYear || 2019}-${month}`}*/}
                                            {/*/>*/}
                                        </Grid>
                                    </Grid>
                                }
                                arrow
                            >
                                <StyledMonthAvatar
                                    style={createMonthStyle(bestMonths[month])}
                                >
                                    {month.slice(0, 1).toUpperCase()}
                                </StyledMonthAvatar>
                            </HtmlTooltip>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    </>)
}
